import { FileText, ListChecks, ToggleRight, PenLine, Plus, Trash2, X, Check } from "lucide-react";

const Q_TYPES = [
  { type: "essay", label: "Essay", icon: FileText, defaultPoints: 20 },
  { type: "short_answer", label: "Written", icon: PenLine, defaultPoints: 10 },
  { type: "multiple_choice", label: "Multiple Choice", icon: ListChecks, defaultPoints: 10 },
  { type: "true_false", label: "True / False", icon: ToggleRight, defaultPoints: 5 },
];

const TYPE_LABEL = {
  essay: "Essay", short_answer: "Written", multiple_choice: "Multiple Choice", true_false: "True / False",
};

let _uid = 0;
const nextId = () => `q_${Date.now()}_${_uid++}`;

export function QuestionBuilder({ questions, setQuestions }) {
  const add = (type) => {
    const def = Q_TYPES.find((t) => t.type === type);
    const base = { id: nextId(), type, question: "", points: def.defaultPoints };
    if (type === "multiple_choice") { base.options = ["", ""]; base.correct_answer = ""; }
    if (type === "true_false") { base.options = ["True", "False"]; base.correct_answer = "True"; }
    if (type === "essay" || type === "short_answer") base.rubric = "";
    setQuestions([...questions, base]);
  };

  const update = (id, patch) => setQuestions(questions.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  const remove = (id) => setQuestions(questions.filter((q) => q.id !== id));

  return (
    <div data-testid="question-builder">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-sm font-medium text-[#3D405B] mr-1">Add a question:</span>
        {Q_TYPES.map((t) => (
          <button key={t.type} type="button" data-testid={`add-q-${t.type}`} onClick={() => add(t.type)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-[#EAE6DF] bg-white hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors">
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {questions.length === 0 ? (
        <p className="text-sm text-[#9CA3AF] italic rounded-xl border border-dashed border-[#EAE6DF] px-4 py-6 text-center">
          No questions yet. Add an Essay, Written, Multiple Choice, or True/False question above — or leave empty for a read‑only assignment.
        </p>
      ) : (
        <div className="space-y-4">
          {questions.map((q, idx) => (
            <div key={q.id} data-testid={`q-card-${idx}`} className="rounded-2xl border border-[#EAE6DF] bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#81B29A]">
                  Q{idx + 1} · {TYPE_LABEL[q.type]}
                </span>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-[#6B7280]">
                    Points
                    <input type="number" min="0" data-testid={`q-points-${idx}`} value={q.points}
                      onChange={(e) => update(q.id, { points: e.target.value })}
                      className="w-16 rounded-lg border border-[#EAE6DF] px-2 py-1 outline-none focus:ring-2 focus:ring-[#81B29A]" />
                  </label>
                  <button type="button" data-testid={`q-remove-${idx}`} onClick={() => remove(q.id)}
                    className="text-[#9CA3AF] hover:text-[#E07A5F]"><Trash2 size={16} /></button>
                </div>
              </div>

              <textarea data-testid={`q-text-${idx}`} value={q.question} onChange={(e) => update(q.id, { question: e.target.value })}
                placeholder={q.type === "essay" ? "Essay prompt…" : "Type your question…"}
                className="w-full min-h-[64px] rounded-xl border border-[#EAE6DF] p-3 outline-none focus:ring-2 focus:ring-[#81B29A] resize-y" />

              {q.type === "multiple_choice" && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-[#6B7280]">Tap the circle to mark the correct answer.</p>
                  {(q.options || []).map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <button type="button" data-testid={`q-correct-${idx}-${oi}`}
                        onClick={() => update(q.id, { correct_answer: opt })}
                        className={`shrink-0 w-6 h-6 rounded-full border-2 grid place-items-center transition-colors ${
                          q.correct_answer && q.correct_answer === opt ? "border-[#81B29A] bg-[#81B29A] text-white" : "border-[#EAE6DF]"}`}>
                        {q.correct_answer === opt && opt ? <Check size={13} /> : null}
                      </button>
                      <input data-testid={`q-opt-${idx}-${oi}`} value={opt}
                        onChange={(e) => {
                          const options = [...q.options];
                          const was = options[oi];
                          options[oi] = e.target.value;
                          const patch = { options };
                          if (q.correct_answer === was) patch.correct_answer = e.target.value;
                          update(q.id, patch);
                        }}
                        placeholder={`Option ${oi + 1}`}
                        className="flex-1 rounded-lg border border-[#EAE6DF] px-3 py-2 outline-none focus:ring-2 focus:ring-[#81B29A]" />
                      {q.options.length > 2 && (
                        <button type="button" onClick={() => update(q.id, { options: q.options.filter((_, x) => x !== oi) })}
                          className="text-[#9CA3AF] hover:text-[#E07A5F]"><X size={15} /></button>
                      )}
                    </div>
                  ))}
                  <button type="button" data-testid={`q-add-opt-${idx}`} onClick={() => update(q.id, { options: [...q.options, ""] })}
                    className="flex items-center gap-1 text-sm text-[#81B29A] hover:underline"><Plus size={14} /> Add option</button>
                </div>
              )}

              {q.type === "true_false" && (
                <div className="mt-3">
                  <p className="text-xs text-[#6B7280] mb-2">Correct answer:</p>
                  <div className="inline-flex rounded-full border border-[#EAE6DF] overflow-hidden">
                    {["True", "False"].map((v) => (
                      <button key={v} type="button" data-testid={`q-tf-${idx}-${v.toLowerCase()}`}
                        onClick={() => update(q.id, { correct_answer: v })}
                        className={`px-5 py-2 text-sm font-medium transition-colors ${q.correct_answer === v ? "bg-[#81B29A] text-white" : "bg-white text-[#6B7280] hover:text-[#3D405B]"}`}>{v}</button>
                    ))}
                  </div>
                </div>
              )}

              {(q.type === "essay" || q.type === "short_answer") && (
                <input data-testid={`q-rubric-${idx}`} value={q.rubric || ""} onChange={(e) => update(q.id, { rubric: e.target.value })}
                  placeholder="Grading notes for the AI (optional) — e.g. full marks if they mention photosynthesis"
                  className="mt-3 w-full rounded-lg border border-[#EAE6DF] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#81B29A]" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function validateQuestions(questions) {
  for (let i = 0; i < questions.length; i++) {
    const q = questions[i];
    if (!q.question.trim()) return `Question ${i + 1} is empty.`;
    if (q.type === "multiple_choice") {
      const opts = (q.options || []).filter((o) => o.trim());
      if (opts.length < 2) return `Question ${i + 1} needs at least 2 options.`;
      if (!q.correct_answer || !opts.includes(q.correct_answer)) return `Mark the correct answer for question ${i + 1}.`;
    }
    if (q.type === "true_false" && !["True", "False"].includes(q.correct_answer)) return `Pick True or False for question ${i + 1}.`;
  }
  return null;
}
